import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, LoadingState, StatusBadge, toneForStatus } from '@ibe/ui';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeleteUser } from '../api/mutations.js';
import { usePropertyOptions, useUser } from '../api/queries.js';
import { type PropertyOption, type UserDetail } from '../api/schemas.js';

/**
 * The user detail screen.
 *
 * Shows the account's identity, roles and property scope, and offers the
 * actions that may be taken on it.
 */

function propertyNames(propertyIds: readonly string[], options: readonly PropertyOption[]): string {
  if (propertyIds.length === 0) {
    return 'All properties (role scope decides)';
  }
  const byId = new Map(options.map((option) => [option.value, option.label]));
  return propertyIds.map((id) => byId.get(id) ?? id).join(', ');
}

export function UserDetailPage(): React.JSX.Element {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const propertyOptions = usePropertyOptions();
  const softDelete = useSoftDeleteUser();
  const [confirming, setConfirming] = useState(false);
  const userQuery = useUser(userId);

  if (propertyOptions.isPending) {
    return <LoadingState label="Loading user" />;
  }

  return (
    <PermissionGuard permission={PERMISSIONS.USER_READ} fallback={<NotPermitted />}>
      <ResourceDetailPage<UserDetail>
        query={userQuery}
        permission={PERMISSIONS.USER_READ}
        parent={{ to: '/users', label: 'Users' }}
        title={(data) => data.displayName}
      >
        {(data) => (
          <>
            <div className="flex gap-2">
              <PermissionGuard permission={PERMISSIONS.USER_WRITE}>
                <Button asChild variant="secondary">
                  <Link to={`/users/${data.id}/edit`}>Edit user</Link>
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setConfirming(true);
                  }}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Delete user
                </Button>
              </PermissionGuard>
            </div>

            <DetailGrid>
              <DetailField label="Email">{data.email}</DetailField>
              <DetailField label="Name">{data.displayName}</DetailField>
              <DetailField label="Status">
                <StatusBadge tone={toneForStatus(data.status)}>{data.status}</StatusBadge>
              </DetailField>
              <DetailField label="Roles">
                {data.roles.join(', ') || 'No roles assigned'}
              </DetailField>
              <DetailField label="Properties">
                {propertyNames(data.propertyIds, propertyOptions.data ?? [])}
              </DetailField>
            </DetailGrid>

            <ConfirmDialog
              open={confirming}
              onOpenChange={setConfirming}
              title="Delete user"
              description={`${data.displayName} will be removed from the user list. Their audit trail and historical actions are retained.`}
              confirmLabel="Delete"
              destructive
              loading={softDelete.isPending}
              onConfirm={() => {
                softDelete.mutate(data.id, {
                  onSuccess: () => {
                    setConfirming(false);
                    void navigate('/users');
                  },
                });
              }}
            />
          </>
        )}
      </ResourceDetailPage>
    </PermissionGuard>
  );
}
